import { ATTESTATION_FILE_SUB_TYPES, DELEGATE_FILE_SUB_TYPES, SIZE_10MO_OCTETS, useTextes } from '@common/config';
import { useDeferredAction, useSessionContext } from '@common/hooks';
import { useOfaFichiers } from '@common/hooks/organisme';
import {
  EMAIL_PLACEHOLDER,
  FIRST_NAME_PLACEHOLDER,
  JOB_PLACEHOLDER,
  LAST_NAME_PLACEHOLDER,
  PHONE_PLACEHOLDER,
} from '@common/schemas';
import {
  FormResultRepresentantDelegataire,
  formSchemaRepresentant,
  formSchemaRepresentantWithDelegataire,
} from '@common/schemas/src/forms/organisme/representant/delegate-representative-form.schema';
import {
  NON_WRITABLE_STATUTS,
  OrganismeFichierDto,
  OrganismeFichierType,
  OrganismeStatutType,
  UpdateOrganismeUtilisateursRepresentantDelegataireArgs,
  useDownloadOrganismeFichierMutation,
  useReplaceOrganismeFichierMutation,
  useUpdateOrganismeFichierMutation,
  useUpdateOrganismeUtilisateursRepresentantDelegataireMutation,
  UtilisateurDto,
} from '@common/services';
import { MapGroup } from '@common/services/session';
import {
  ConfirmationModal,
  DsfrModal,
  EmailField,
  FileUploadField,
  Form,
  getModalById,
  JPG_PNG_PDF_EXTENSIONS,
  JPG_PNG_PDF_FILE_TYPES,
  PhoneField,
  SavedFile,
  TextField,
} from '@common/ui';
import { appToast } from '@common/ui/toast';
import { areSomePropertiesDifferent, isSchemaIncludeAnOther } from '@common/utils';
import { fetchLocalFile, toSavedFile } from '@common/utils/file';
import React, { useEffect, useRef, useState } from 'react';
import { ZodObject } from 'zod';
import { FormMethodsRef } from '../../form';
import { mapEditRlFormResult } from './utils/edit-rl-mapper';

const confirmationModal = getModalById('edit-rl-delegataire-confirmation-modal');

export const EditOfaRlModal = ({
                                 dsfrModal,
                                 organismeId,
                                 representant,
                                 delegataire,
                                 organismeStatut,
                               }: EditRLInfoModalProps) => {
  const t = useTextes('Commun', 'Representants');

  const { groups } = useSessionContext();

  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null!);
  const formMethodsRef = useRef<FormMethodsRef<FormResultRepresentantDelegataire> | null>(null);

  const [withDelegate, setWithDelegate] = useState<boolean>(!!delegataire);
  const [formSchema, setFormSchema] = useState<ZodObject>((withDelegate) ? formSchemaRepresentantWithDelegataire : formSchemaRepresentant);
  const [isLastNameChanging, setIsLastNameChanging] = useState<boolean>(false);

  const { filesMap, isLoading } = useOfaFichiers({ organismeId });
  const { prepareAction, performAction, clearAction, actionExists, isPerforming } = useDeferredAction();

  const [updateRepresentantDelegataireUtilisateur] = useUpdateOrganismeUtilisateursRepresentantDelegataireMutation();

  const [replaceOrganismeFichier] = useReplaceOrganismeFichierMutation();
  const [updateOrganismeFichier] = useUpdateOrganismeFichierMutation();
  const [downloadOrganismeFichier] = useDownloadOrganismeFichierMutation();

  useEffect(() => {
    setFormSchema((withDelegate) ? formSchemaRepresentantWithDelegataire : formSchemaRepresentant);
  }, [withDelegate]);

  if (isLoading) return null;

  const delegationFile = filesMap.DELEGATION_POUVOIR;
  const attestationFile = filesMap.ATTESTATION_REPRESENTANT;

  const defaultValues: FormResultRepresentantDelegataire = {
    id: representant.id,
    lastName: representant.nom,
    firstName: representant.prenom,
    job: representant.travail,
    phone: representant.telephone,
    email: representant.email,
    legalFile: (attestationFile) ? toSavedFile(attestationFile) : null!,

    delegateId: delegataire?.id,
    delegateLastName: delegataire?.nom,
    delegateFirstName: delegataire?.prenom,
    delegateEmail: delegataire?.email,
    delegateJob: delegataire?.travail,
    delegateFile: (delegationFile) ? toSavedFile(delegationFile) : undefined,
  } as FormResultRepresentantDelegataire;

  async function handleFileChanges(file: OrganismeFichierDto | undefined, savedFile: SavedFile, fichierType: OrganismeFichierType) {
    if (!savedFile) return;

    // Si le fichier ne possède pas un ID, c'est qu'il a été màj côté formulaire, donc on le pousse côté backend
    if (!savedFile.id && savedFile.url) {
      const localFile: File | null = await fetchLocalFile(savedFile.url, savedFile.name);
      if (!localFile) return appToast.error('Impossible de récupérer le fichier déposé.');

      const formData = new FormData();
      formData.set('file', localFile);
      formData.set('fileSubType', savedFile.type);

      await replaceOrganismeFichier({ id: organismeId, fichierType, formData }).unwrap();
      return;
    }
    // si on change uniquement le type de fichier
    if (file && savedFile.id && file.subType !== savedFile.type) {
      await updateOrganismeFichier({ id: organismeId, fichierType, fichierSubType: savedFile.type }).unwrap();
    }
  }

  const checkFilesChanges = async (legalFile: SavedFile, delegateFile: SavedFile) => {
    await handleFileChanges(attestationFile, legalFile, OrganismeFichierType.ATTESTATION_REPRESENTANT);
    await handleFileChanges(delegationFile, delegateFile, OrganismeFichierType.DELEGATION_POUVOIR);
  };

  async function handleSubmit(result: FormResultRepresentantDelegataire) {
    const {
      representant: representantResult,
      delegataire: delegataireResult,
      legalFile,
      delegateFile,
    } = mapEditRlFormResult(result);

    // on vérifie s'il y a eu des changements concernant les données du representant ou du delegataire

    // Conditions qui nécessitent une double confirmation
    const isRepresentantNameChange = representant.nom !== representantResult?.nom;
    const isDelegataireNameChange = delegataire && delegataireResult && delegataire.nom !== delegataireResult?.nom;
    const isDelegataireAdded = !delegataire && delegataireResult;
    const isDelegataireDeleted = delegataire && !delegataireResult;

    // Conditions qui ne nécessitent pas une double confirmation
    const isRepresentantChangeExceptNom = areSomePropertiesDifferent(representant, representantResult, ['prenom', 'travail', 'telephone', 'email']) && !isRepresentantNameChange;
    const isDelegataireChangeExceptNom = delegataire && delegataireResult && areSomePropertiesDifferent(delegataire, delegataireResult, ['prenom', 'travail', 'telephone', 'email']) && !isDelegataireNameChange;

    const updateUsersReqBody: UpdateOrganismeUtilisateursRepresentantDelegataireArgs = {
      id: organismeId,
      utilisateursRepresentantDelegataireDto: {
        representant: representantResult,
        delegataire: delegataireResult,
      },
    };

    if (isRepresentantNameChange || isDelegataireNameChange || isDelegataireAdded || isDelegataireDeleted) {
      prepareAction(async () => {
        await updateRepresentantDelegataireUtilisateur(updateUsersReqBody);
        await checkFilesChanges(legalFile, delegateFile);
      });

      // appeler la modal de confirmation qui validera ou non la suppression de du delegataire et la sauvegarde de changements
      confirmationModal.open();
    }
    // s'il y a un changement du representant ou du delegataire, ou un ajout de delegataire
    else if (isRepresentantChangeExceptNom || isDelegataireChangeExceptNom) {
      await updateRepresentantDelegataireUtilisateur(updateUsersReqBody);
      await checkFilesChanges(legalFile, delegateFile);

      setIsLastNameChanging(false);
    }
    // on vérifie et fait les appelle si les fichiers ont changé
    else {
      await checkFilesChanges(legalFile, delegateFile);
    }

    dsfrModal.close();
  }

  const handleSecondModalConfirmation = async () => {
    await performAction();
    setIsLastNameChanging(false);
    confirmationModal.close();
  };

  const handleCancelDelegateDeletion = () => {
    confirmationModal.close();
    if (actionExists) {
      // Si une action est toujours enregistrée dans le hook, ça veut dire qu'elle n'a pas été consommée
      // Et il faut donc rouvrir la modale principale
      dsfrModal.open();
    }
    clearAction();
  };

  return (
    <>
      <dsfrModal.Component
        title={''}
        size="large"
        topAnchor={false}
        buttons={[
          {
            children: 'ANNULER',
            className: 'text-red-400!',
            doClosesModal: true,
            priority: 'tertiary',
            onClick: () => formMethodsRef.current?.reset(defaultValues),
          },
          {
            children: 'VALIDER',
            doClosesModal: false,
            priority: 'primary',
            disabled: isPerforming || (groups.includes(MapGroup.MAP_OFA) && !groups.includes(MapGroup.MAP_FC) && NON_WRITABLE_STATUTS.includes(organismeStatut)),
            onClick: () => buttonSubmitRef.current?.click(),
          },
        ]}
      >
        <div className={'flex flex-col p-6'}>

          <div className={'flex flex-col gap-6'}>

            <h4 className={'m-0!'}>Modification du représentant légal</h4>

            <span className={'text-sm text-[#484D7A] mb-10'}>Les champs marqués d’un * sont obligatoires</span>
          </div>

          <Form<FormResultRepresentantDelegataire> className={'flex flex-col gap-4'}
                                                   onSubmit={handleSubmit}
                                                   schema={formSchema}
                                                   defaultValues={defaultValues}
                                                   buttonSubmitRef={buttonSubmitRef}
                                                   methodsRef={formMethodsRef}>

            <TextField name={'lastName'} title={'Nom'} placeholder={LAST_NAME_PLACEHOLDER}
                       tooltip={t['DONNEE_OFA_COMP_RL_NOM_INFO_BULLE_MSG']}
                       onChange={() => setIsLastNameChanging(true)} />

            <TextField name={'firstName'} title={'Prénom'} placeholder={FIRST_NAME_PLACEHOLDER}
                       tooltip={t['DONNEE_OFA_COMP_RL_PRENOM_INFO_BULLE_MSG']} />

            <TextField name={'job'} title={'Fonction'} placeholder={JOB_PLACEHOLDER} />

            <PhoneField name={'phone'} title={'Téléphone'} placeholder={PHONE_PLACEHOLDER}
                        tooltip={t['DONNEE_OFA_COMP_RL_TELEPHONE_INFO_BULLE_MSG']} />

            <EmailField name={'email'} title={'Courriel'} placeholder={EMAIL_PLACEHOLDER}
                        tooltip={t['DONNEE_OFA_COMP_RL_COURRIEL_INFO_BULLE_MSG']} />

            <FileUploadField name="legalFile"
                             title="Document Attestant de la qualité du représentant légal"
                             subtitle="Kbis, Statut, PV d'AG, décret ou arrêté"
                             requiredErrorMsg={t['REPR_MES11']}
                             typesSelection={ATTESTATION_FILE_SUB_TYPES}
                             extensions={JPG_PNG_PDF_EXTENSIONS}
                             fileTypes={JPG_PNG_PDF_FILE_TYPES}
                             maxSize={SIZE_10MO_OCTETS}
                             fallbackDownloadFnIfUrlIsEmpty={() => downloadOrganismeFichier({
                               id: organismeId,
                               fichierType: OrganismeFichierType.ATTESTATION_REPRESENTANT,
                             })} />

            <label>
              <input type="checkbox" checked={withDelegate} onChange={(ev) => setWithDelegate(ev.target.checked)} />
              &nbsp;&nbsp;délégation de pouvoir ou de signature
            </label>

            {(withDelegate && isSchemaIncludeAnOther(formSchema, formSchemaRepresentantWithDelegataire) &&
              <div className={'flex flex-col gap-4 mt-6'}>

                <div className={'flex flex-col gap-6'}>

                  <h5 className={'m-0!'}>Modification du délégataire</h5>

                  <span className={'text-sm text-[#484D7A] mb-10'}>Les champs marqués d’un * sont obligatoires</span>
                </div>

                <div className={'flex flex-col gap-4'}>

                  <TextField name={'delegateLastName'}
                             title={'Nom'}
                             placeholder={LAST_NAME_PLACEHOLDER}
                             onChange={() => setIsLastNameChanging(true)} />

                  <TextField name={'delegateFirstName'}
                             title={'Prénom'}
                             placeholder={FIRST_NAME_PLACEHOLDER} />

                  <TextField name={'delegateJob'} title={'Fonction'} placeholder={JOB_PLACEHOLDER} />

                  <EmailField name={'delegateEmail'} title={'Courriel'} placeholder={EMAIL_PLACEHOLDER} />

                  <FileUploadField name="delegateFile"
                                   title="Délégation de pouvoir"
                                   subtitle="Déclaration sur l'honneur"
                                   requiredErrorMsg={t['REPR_MES16']}
                                   typesSelection={DELEGATE_FILE_SUB_TYPES}
                                   extensions={JPG_PNG_PDF_EXTENSIONS}
                                   fileTypes={JPG_PNG_PDF_FILE_TYPES}
                                   maxSize={SIZE_10MO_OCTETS}
                                   fallbackDownloadFnIfUrlIsEmpty={() => downloadOrganismeFichier({
                                     id: organismeId,
                                     fichierType: OrganismeFichierType.DELEGATION_POUVOIR,
                                   })} />
                </div>
              </div>
            )}
          </Form>

          {isLastNameChanging && (
            <span
              className={'mt-6 text-red-500'}>{'Attention : en cas de modification (représentant légal ou d\'un délégataire), la fiche d’habilitation est mise à jour. Le représentant légal la valide par courriel, puis elle est validée par France compétences. Pendant ce délai, l’accès à MAP est indisponible.'}</span>
          )}

        </div>
      </dsfrModal.Component>
      <ConfirmationModal dsfrModal={confirmationModal}
                         description={t['REPR_MODIF_INFO_BULLE_MSG']}
                         subDescription={t['REPR_MODIF_INFO_BULLE_SUB_MSG']}
                         confirmProps={{
                           onClick: () => handleSecondModalConfirmation(),
                           disabled: isPerforming || (groups.includes(MapGroup.MAP_OFA) && !groups.includes(MapGroup.MAP_FC) && NON_WRITABLE_STATUTS.includes(organismeStatut)),
                         }}
                         cancelProps={{ onClick: () => handleCancelDelegateDeletion(), disabled: isPerforming }} />
    </>
  );
};

interface EditRLInfoModalProps {
  dsfrModal: DsfrModal;
  organismeId: number;
  organismeStatut: OrganismeStatutType;
  representant: UtilisateurDto;
  delegataire?: UtilisateurDto;
}
